import { useEffect, useState } from "react";
import Layout from "../components/Layout";
import { API } from "../App";
import axios from "axios";
import { Plus, Trash2, TrendingUp, TrendingDown, X } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { format } from "date-fns";
import { tr } from "date-fns/locale";

const kategoriler = {
  gelir: ["Satış", "Hizmet", "Faiz", "Diğer Gelir"],
  gider: ["Maaş", "Kira", "Fatura", "Malzeme", "Vergi", "Diğer Gider"]
};

const GelirGider = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [filter, setFilter] = useState("hepsi");
  const [form, setForm] = useState({
    tip: "gelir",
    kategori: "",
    aciklama: "",
    tutar: "",
    tarih: new Date()
  });

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${API}/gelir-gider`, { withCredentials: true });
      setItems(response.data);
    } catch (error) {
      toast.error("Veriler yüklenirken hata oluştu");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.kategori || !form.tutar) {
      toast.error("Lütfen tüm alanları doldurun");
      return;
    }

    try {
      await axios.post(`${API}/gelir-gider`, {
        ...form,
        tutar: parseFloat(form.tutar),
        tarih: form.tarih.toISOString()
      }, { withCredentials: true });
      
      toast.success(`${form.tip === 'gelir' ? 'Gelir' : 'Gider'} eklendi`);
      setDialogOpen(false);
      setForm({ tip: "gelir", kategori: "", aciklama: "", tutar: "", tarih: new Date() });
      fetchItems();
    } catch (error) {
      toast.error("Kayıt eklenirken hata oluştu");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Bu kaydı silmek istediğinize emin misiniz?")) return;
    
    try {
      await axios.delete(`${API}/gelir-gider/${id}`, { withCredentials: true });
      toast.success("Kayıt silindi");
      fetchItems();
    } catch (error) {
      toast.error("Silme işlemi başarısız");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(value || 0);
  };

  const filteredItems = filter === "hepsi" ? items : items.filter(item => item.tip === filter);
  
  const toplamGelir = items.filter(i => i.tip === "gelir").reduce((sum, i) => sum + i.tutar, 0);
  const toplamGider = items.filter(i => i.tip === "gider").reduce((sum, i) => sum + i.tutar, 0);

  if (loading) {
    return (
      <Layout>
        <div className="p-8 flex items-center justify-center min-h-screen">
          <div className="w-8 h-8 border-2 border-[#002FA7] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="p-6 lg:p-8" data-testid="gelir-gider-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-[#0A0A0B] tracking-tight">
              Gelir / Gider
            </h1>
            <p className="text-[#5E5E62] text-sm mt-1">
              Tüm gelir ve giderlerinizi yönetin
            </p>
          </div>
          
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button className="btn-primary flex items-center gap-2" data-testid="add-gelir-gider-btn">
                <Plus size={18} />
                Yeni Kayıt
              </button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle className="font-heading">Yeni Gelir/Gider Ekle</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                {/* Tip Selection */}
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, tip: "gelir", kategori: "" })}
                    className={`py-3 rounded-sm font-medium transition-colors ${
                      form.tip === "gelir"
                        ? "bg-[#E6F9F0] text-[#00C566] border-2 border-[#00C566]"
                        : "bg-[#F7F7F8] text-[#5E5E62] border border-[#E4E4E7]"
                    }`}
                    data-testid="select-gelir-btn"
                  >
                    <TrendingUp size={18} className="inline mr-2" />
                    Gelir
                  </button>
                  <button
                    type="button"
                    onClick={() => setForm({ ...form, tip: "gider", kategori: "" })}
                    className={`py-3 rounded-sm font-medium transition-colors ${
                      form.tip === "gider"
                        ? "bg-[#FFEBEE] text-[#FF3B30] border-2 border-[#FF3B30]"
                        : "bg-[#F7F7F8] text-[#5E5E62] border border-[#E4E4E7]"
                    }`}
                    data-testid="select-gider-btn"
                  >
                    <TrendingDown size={18} className="inline mr-2" />
                    Gider
                  </button>
                </div>

                {/* Kategori */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Kategori</label>
                  <Select value={form.kategori} onValueChange={(value) => setForm({ ...form, kategori: value })}>
                    <SelectTrigger data-testid="kategori-select">
                      <SelectValue placeholder="Kategori seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {kategoriler[form.tip].map((kat) => (
                        <SelectItem key={kat} value={kat}>{kat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Tutar */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tutar (₺)</label>
                  <input
                    type="number"
                    step="0.01"
                    value={form.tutar}
                    onChange={(e) => setForm({ ...form, tutar: e.target.value })}
                    className="input-default"
                    placeholder="0.00"
                    data-testid="tutar-input"
                  />
                </div>

                {/* Tarih */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Tarih</label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <button type="button" className="input-default text-left" data-testid="tarih-picker">
                        {format(form.tarih, "d MMMM yyyy", { locale: tr })}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={form.tarih}
                        onSelect={(date) => date && setForm({ ...form, tarih: date })}
                        locale={tr}
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                {/* Açıklama */}
                <div>
                  <label className="block text-sm font-medium text-[#0A0A0B] mb-1">Açıklama</label>
                  <input
                    type="text"
                    value={form.aciklama}
                    onChange={(e) => setForm({ ...form, aciklama: e.target.value })}
                    className="input-default"
                    placeholder="Opsiyonel"
                    data-testid="aciklama-input"
                  />
                </div>

                <button type="submit" className="btn-primary w-full" data-testid="submit-gelir-gider-btn">
                  Kaydet
                </button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingUp size={18} className="text-[#00C566]" />
              <span className="text-sm text-[#5E5E62]">Toplam Gelir</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#00C566]">{formatCurrency(toplamGelir)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <div className="flex items-center gap-2 mb-2">
              <TrendingDown size={18} className="text-[#FF3B30]" />
              <span className="text-sm text-[#5E5E62]">Toplam Gider</span>
            </div>
            <p className="font-mono text-xl font-medium text-[#FF3B30]">{formatCurrency(toplamGider)}</p>
          </div>
          <div className="bg-white border border-[#E4E4E7] rounded-sm p-4">
            <span className="text-sm text-[#5E5E62] block mb-2">Net Durum</span>
            <p className={`font-mono text-xl font-medium ${toplamGelir - toplamGider >= 0 ? 'text-[#00C566]' : 'text-[#FF3B30]'}`}>
              {formatCurrency(toplamGelir - toplamGider)}
            </p>
          </div>
        </div>

        {/* Filter */}
        <div className="flex gap-2 mb-6">
          {["hepsi", "gelir", "gider"].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-sm transition-colors ${
                filter === f
                  ? "bg-[#002FA7] text-white"
                  : "bg-white text-[#5E5E62] border border-[#E4E4E7] hover:bg-[#F7F7F8]"
              }`}
              data-testid={`filter-${f}`}
            >
              {f === "hepsi" ? "Hepsi" : f === "gelir" ? "Gelirler" : "Giderler"}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="bg-white border border-[#E4E4E7] rounded-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="gelir-gider-table">
              <thead>
                <tr className="border-b border-[#E4E4E7]">
                  <th className="table-header">Tip</th>
                  <th className="table-header">Kategori</th>
                  <th className="table-header">Açıklama</th>
                  <th className="table-header">Tutar</th>
                  <th className="table-header">Tarih</th>
                  <th className="table-header w-16"></th>
                </tr>
              </thead>
              <tbody>
                {filteredItems.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="py-12 text-center text-[#5E5E62]">
                      Henüz kayıt bulunmuyor
                    </td>
                  </tr>
                ) : (
                  filteredItems.map((item) => (
                    <tr key={item.id} className="table-row">
                      <td className="table-cell">
                        <span className={item.tip === "gelir" ? "badge-paid" : "badge-overdue"}>
                          {item.tip === "gelir" ? "Gelir" : "Gider"}
                        </span>
                      </td>
                      <td className="table-cell font-medium">{item.kategori}</td>
                      <td className="table-cell text-[#5E5E62]">{item.aciklama || "-"}</td>
                      <td className={`table-cell font-mono ${item.tip === "gelir" ? "text-[#00C566]" : "text-[#FF3B30]"}`}>
                        {item.tip === "gelir" ? "+" : "-"}{formatCurrency(item.tutar)}
                      </td>
                      <td className="table-cell font-mono text-[#5E5E62]">
                        {new Date(item.tarih).toLocaleDateString('tr-TR')}
                      </td>
                      <td className="table-cell">
                        <button
                          onClick={() => handleDelete(item.id)}
                          className="p-2 text-[#FF3B30] hover:bg-[#FFEBEE] rounded-sm transition-colors"
                          data-testid={`delete-${item.id}`}
                        >
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default GelirGider;
